export const WHO_WE_HELP_SCHEMA = {
  en: {
    title: "Who We Help",
    sub_title:
      "Built for real financial lives. Designed for the people and decisions that shape them.",
    hero_ctas: [
      { text: "Create Your Account", link: "/sign-up", type: "primary_light" },
      { text: "Sign In", link: "/sign-in", type: "secondary" },
    ],
    hero_cards: [
      {
        targetId: "financial-insights",
        icon: "/images/icons/chart_bar_1F93FF.svg",
        icon_alt: "Financial Insights",
        heading: "Financial Insights",
        description: "I want to understand my money and make better decisions.",
      },
      {
        targetId: "comple-tax-situation",
        icon: "/images/icons/calculator_blue.svg",
        icon_alt: "Complex Tax",
        heading: "Complex Tax",
        description:
          "I have a business, investments, or multiple income streams.",
      },
      {
        targetId: "affiliates-content-creators",
        icon: "/images/icons/campaign_voice_blue.svg",
        icon_alt: "Creators & Influencers",
        heading: "Creators & Influencers",
        description: "I have an audience and want to monetize responsibly.",
      },
    ],
    identifier_sections: [
      {
        id: "comple-tax-situation",
        image: "/images/spending_hero.png",
        image_alt: "Financial Insights",
        heading: "Understand what's happening behind your money.",
        paragraphs: [
          "Plaintr helps you see how your spending, subscriptions, income, and habits connect so you can make decisions with confidence.",
          "Track your financial life in one place, create budgets that fit your goals, and understand where your money is actually going.",
          "Not just numbers. Real clarity for everyday decisions.",
        ],
        list_items: [
          "See all your finances in one place.",
          "Create budgets that match your lifestyle.",
          "Understand patterns, spending, and recurring expenses.",
        ],
        ctas: [
          {
            text: "Explore Insights",
            link: "/financial-insights",
            type: "secondary",
          },
          { text: "Get Started", link: "/sign-up", type: "primary" },
        ],
      },
      {
        id: "comple-tax-situation",
        image: "/images/hero_man.svg",
        image_alt: "People With Complex Tax Situations",
        heading:
          "When your taxes involve more than a simple return, precision matters.",
        paragraphs: [
          "Plaintr matches you with a Certified Expert whose background aligns with your specific situation. Before anything is filed, your case is reviewed carefully, your questions are addressed directly, and you understand exactly how your return is handled.",
          "No guesswork. No generic software.",
          "Just qualified oversight built for real-world complexity.",
        ],
        list_items: [
          "Matched with a Certified Expert aligned to your case.",
          "Clear review and approval before filing.",
          "Secure communication from start to finish.",
          "Final approval before anything is submitted",
        ],
        ctas: [
          { text: "Learn More", link: "/tax", type: "secondary" },
          { text: "Start Filing", link: "/sign-up", type: "primary" },
        ],
      },
      {
        id: "affiliates-content-creators",
        image: "/images/affiliate_hero.svg",
        image_alt: "Content & Community Builders",
        heading:
          "If you’ve built an audience, you’ve built something valuable.",
        paragraphs: [
          "Plaintr gives you a clear way to earn from that audience by introducing a financial service backed by Certified Experts. When someone you refer completes a real service, you earn through a transparent and trackable system.",
          "No vague commissions. No inflated promises.",
          "Just measurable earnings tied to real services.",
        ],
        list_items: [
          "Unique tracking link with performance visibility.",
          "Clear earnings tied to completed services.",
          "A professional financial service you can confidently share.",
        ],
        ctas: [
          {
            text: "Learn More",
            link: "/services?filter=partnership",
            type: "secondary",
          },
          { text: "Sign Up", link: "/sign-up", type: "primary" },
        ],
      },
    ],
    hero_paragraphs: [
      "Plaintr connects you with certified tax professionals — CPAs, Enrolled Agents, and Tax Attorneys — who understand the realities of modern work, remote life, equity, side income, and multi-state mobility.",
      " If your taxes are more than “just a W-2,” this is for you.",
    ],
    section_1: {
      useCases: [
        {
          id: "multistate-filers",
          title: "Multistate Filers",
          subtitle: "Remote work across state lines",
          // Preview (collapsed)
          content:
            "Working in one state and living in another? Moved mid-year? Earning equity from a company based somewhere else? Multistate taxes get complicated fast — and mistakes get expensive.",
          // Expanded content (for future detailed views)
          details: [
            "Remote work changed everything.",
            "Thousands of people now live in one state, work for a company in another, earn equity sourced to different jurisdictions, or move mid-year — and each of these situations creates tax obligations most people never learn about.",
            "A certified professional helps you avoid double taxation, missed credits, and filing errors while making sure every state gets exactly what it should (no more, no less).",
          ],
          bullets_title: "We help multistate filers understand:",
          bullets: [
            "Which state can legally tax your income",
            "How to avoid being taxed twice when two states claim you",
            "How mid-year moves and residency changes affect your return",
            "How RSUs, ESPP, and bonus income get sourced across states",
          ],
          image: `${process.env.PUBLIC_URL}/images/multi_state.svg`,
          readMore: {
            text: "Start your multistate filing",
            link: "/sign-up",
          },
        },
        {
          id: "crypto-stock-investors",
          title: "Crypto, Equity & Investment Filers",
          subtitle: "Modern investments across multiple platforms",
          content:
            "If you traded stocks, sold crypto, or received equity compensation, your tax return needs more than a simple upload. Correct reporting protects you from IRS mismatches and penalties.",
          details: [
            "Investment activity has outgrown traditional tax tools.",
            "From crypto sales to high-volume stock trades to RSUs vesting mid-year, every transaction creates a tax footprint — and missing even one line item can trigger IRS notices.",
            "Our certified professionals know how to reconcile multiple 1099s, track cost basis, handle wash-sale rules, and classify digital asset transactions correctly.",
          ],
          bullets_title: "We help investors file with confidence:",
          bullets: [
            "Equity compensation (RSUs, ESPP, stock options)",
            "Brokerage accounts with high transaction volume",
            "Crypto activity across exchanges",
            "NFT sales, staking income, and digital asset gains",
          ],
          image: `${process.env.PUBLIC_URL}/images/crypto_invest.svg`,
          readMore: {
            text: "Start your investment filing",
            link: "/sign-up",
          },
        },
        {
          id: "self-employed-small-business",
          title: "Self-Employed & Small Business Filers",
          subtitle: "Independent work with real-world deductions that matter",
          content:
            "If your income comes from clients — not payroll — your taxes require accuracy. Whether you're a freelancer, contractor, creator, or small business owner, a certified pro helps you keep more of what you earn.",
          details: [
            "Independent workers and small business owners often pay more tax than they should — not because of the IRS, but because generic software doesn't guide them clearly.",
            "A certified professional helps you identify deductions, separate personal and business expenses, and structure your year so you're protected and optimized.",
          ],
          category_includes:
            "This category includes: Freelancers, contractors, creators, online sellers, single-member LLCs, and small business owners.",
          bullets_title: "We help you handle:",
          bullets: [
            "1099-NEC, 1099-K, and Schedule C reporting",
            "Maximizing business deductions",
            "Classifying expenses properly",
            "Estimating quarterly taxes",
            "Distinguishing personal vs. business expenses",
          ],
          image: `${process.env.PUBLIC_URL}/images/self_employee.svg`,
          readMore: {
            text: "Start your self-employed filing",
            link: "/sign-up",
          },
        },
        {
          id: "professionals-complex-documents",
          title: "Professionals With Complex Documents",
          subtitle: "High-documentation roles needing accuracy",
          content:
            "Some careers generate complex, multi-form tax situations — often with income in multiple categories. If your return requires careful coordination of multiple documents, a certified pro is essential.",
          details: [
            "Teachers with stipends, nurses with travel income, consultants billing across states, software engineers with equity compensation, and corporate employees with bonuses or fringe benefits — all fall into this group.",
            "Complexity isn’t a problem; filing it wrong is.",
          ],
          bullets_title: "We help professionals with:",
          bullets: [
            "Multiple W-2s and multi-source income",
            "Equity compensation + salaried income",
            "Travel stipends, per diem, and reimbursements",
            "Royalty income, consulting income, and side teaching",
          ],
          image: `${process.env.PUBLIC_URL}/images/professionals.svg`,
          readMore: {
            text: "Start your professional filing",
            link: "/sign-up",
          },
        },
        {
          id: "property-owners-landlords",
          title: "Property Owners & Landlords",
          subtitle: "Rental income with rules that actually matter",
          content:
            "Rental income creates opportunity — and paperwork. A certified pro helps you capture every allowed expense, stay audit-safe, and report property activity correctly.",
          details: [
            "Rental properties bring recurring income, upkeep costs, depreciation schedules, and legal distinctions that affect your tax outcome.",
            "Misclassifying repairs, improvements, or passive activity can cost you thousands.",
            "A certified pro ensures every detail is reported correctly and to your benefit.",
          ],
          bullets_title: "We help property owners:",
          bullets: [
            "Report rental income and expenses",
            "Handle depreciation schedules",
            "Distinguish repairs vs. improvements",
            "Manage multi-property filings",
          ],
          image: `${process.env.PUBLIC_URL}/images/home_invest.svg`,
          readMore: {
            text: "Start your rental filing",
            link: "/sign-up",
          },
        },
        {
          id: "advanced-high-complexity",
          title: "Advanced / High-Complexity Filers",
          subtitle: "When multiple financial layers overlap",
          content:
            "If your tax life includes multiple income streams, international elements, trusts, or layered financial activity, organized guidance ensures everything is captured the right way.",
          details: [
            "Some tax situations demand expert coordination:",
            "High-income households, blended families, K-1 investors, trust income recipients, retirees with multiple pensions, or individuals with foreign accounts or multi-source investment activity.",
            "This category exists so you don't have to guess.",
          ],
          bullets_title: "We help high-complexity filers with:",
          bullets: [
            "K-1s, partnerships, and trust income",
            "Multiple retirement income sources",
            "International reporting requirements",
            "Multi-stream household income",
            "Large deductions and multi-form filings",
          ],
          image: `${process.env.PUBLIC_URL}/images/complex.svg`,
          readMore: {
            text: "Start your advanced filing",
            link: "/sign-up",
          },
        },
      ],
    },
    cta_suggestions: {
      intro: "Find your match",
      links: [{ text: "Start Filing", link: "/sign-up", stl: "b" }],
    },
  },
};
